import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, ListFilter, Search, Trash, TriangleAlert, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DropdownCard } from '@trellixio/roaster-coffee';
import { toast } from 'sonner';
import Page from 'components/shared/Page';
import Pagination from 'components/shared/Pagination';
import ConfirmationModal from 'components/shared/ConfirmationModal';
import EmptyCard from 'components/shared/EmptyCard';
import FilterSelect from 'components/shared/FilterSelect';
import { LockableButton } from 'components/shared/Lockable';
import AssociatedPilgrimsList from 'components/shared/AssociatedPilgrimsList';
import { useAPI, useDebounce, useGetUser } from 'hooks';
import { formatDate, formatNumber, preventSubmitWithENTER, createDownloadable } from 'helpers';
import Locales from 'locales';
import { Input } from 'components/ui/input';
import { Badge } from 'components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Button } from 'components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { Alert, AlertTitle } from 'components/ui/alert';
import EcobankPaymentModal from './EcobankPaymentModal';
import { useAGLOCampaign, useCampaignSwitchStatus } from 'hooks/useAGLOCampaign';
import HistoricalCampaignStrip from 'components/shared/HistoricalCampaignStrip';
import {
  ECOBANK_DEVELOPMENT,
  ECOBANK_PAYMENT_STATE,
  canStartEcobankPayment,
  getEcobankExpandedCopy,
  getEcobankRowStatus,
  isEcobankTransactionLocked,
  resolveEcobankPaymentState,
} from './ecobankPaymentState';

const CONST_NONE = 'none';
const CONST_FILTER_LABEL_AGENCY = 'agency';
const CONST_TOTAL_SEATS = 2300;
const ECOBANK_LOCALES = Locales.invoice.ecobank;
const getInvoiceAppliedFilters = (agency) =>
  agency && agency !== CONST_NONE ? { [CONST_FILTER_LABEL_AGENCY]: agency } : null;

const InvoiceItem = ({
  invoice,
  isAgency,
  isAgencyPrimary,
  isBank,
  ecobankPaymentState,
  onDelete,
  onActivate,
  onEcobankAction,
  invoiceValidationMethod,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const pilgrimCount = invoice.trips?.length || invoice.deposit_count || 0;
  const detailId = `invoice-details-${invoice.id}`;
  const columnCount = isAgency || isBank ? 10 : 9;
  const validator = invoice.is_paid ? invoiceValidationMethod(invoice) : '-';
  const isEcobankPayable = !invoice.is_paid && !invoice.quota_reached_error;
  const ecobankRowStatus = isAgency ? getEcobankRowStatus(ecobankPaymentState) : null;
  const ecobankExpandedCopy = isAgency ? getEcobankExpandedCopy(ecobankPaymentState) : null;
  const isEcobankLocked = isAgency && isEcobankTransactionLocked(ecobankPaymentState);
  const canStartPayment = canStartEcobankPayment(ecobankPaymentState);
  const canContinuePayment = ecobankPaymentState === ECOBANK_PAYMENT_STATE.AUTHORIZATION_REQUIRED;

  return (
    <>
      <TableRow
        className="h-[50px] cursor-pointer select-none bg-white text-sm hover:bg-[#fbfcfb]"
        onClick={() => setIsExpanded((previous) => !previous)}
      >
        <TableCell className="w-12 px-2 py-0 text-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-[30px] w-[30px] text-[#718178] shadow-none hover:bg-[#f0f2f1] hover:text-[#4b5850]"
            onClick={(event) => {
              event.stopPropagation();
              setIsExpanded((previous) => !previous);
            }}
            aria-expanded={isExpanded}
            aria-controls={detailId}
            aria-label={`${isExpanded ? 'Masquer' : 'Afficher'} les pèlerins associés à la facture ${invoice.code}`}
          >
            <ChevronRight
              className={`size-4 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
              aria-hidden="true"
            />
          </Button>
        </TableCell>
        <th scope="row" className="whitespace-nowrap px-3 py-0 text-left align-middle font-semibold text-[#34483a]">
          <span>{invoice.code}</span>
        </th>
        <TableCell className="px-3 py-0">
          <span className="block truncate" title={invoice.agency_name || undefined}>
            {invoice.agency_name || '-'}
          </span>
        </TableCell>
        <TableCell className="px-3 py-0 text-right font-medium tabular-nums">
          {`${formatNumber(invoice.amount)} F CFA`}
        </TableCell>
        <TableCell className="px-3 py-0 text-center tabular-nums">{invoice.deposit_count}</TableCell>
        <TableCell className="px-3 py-0">{formatDate(invoice.created)}</TableCell>
        <TableCell className="px-3 py-0">{invoice.paid ? formatDate(invoice.paid) : '-'}</TableCell>
        <TableCell className="px-3 py-0">
          <span className="block truncate" title={validator === '-' ? undefined : validator}>
            {validator}
          </span>
        </TableCell>
        <TableCell className="px-3 py-0">
          {invoice.quota_reached_error && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge variant="destructive">Quota dépassé</Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Aucune place disponible pour un ou plusieurs pèlerins associés à cette facture.</p>
              </TooltipContent>
            </Tooltip>
          )}

          {!invoice.quota_reached_error && !invoice.is_paid && (
            <div>
              <Badge variant="pending">En attente</Badge>
              {ecobankRowStatus && <span className="mt-0.5 block text-xs text-[#677b87]">{ecobankRowStatus}</span>}
            </div>
          )}

          {invoice.is_paid && <Badge variant="success">Validé</Badge>}
        </TableCell>
        {(isAgency || isBank) && (
          <TableCell className="px-3 py-0" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center gap-2">
              {isAgencyPrimary && isEcobankPayable && (canStartPayment || canContinuePayment) && (
                <LockableButton
                  variant="hajj"
                  size="sm"
                  className="whitespace-nowrap shadow-none"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEcobankAction(invoice);
                  }}
                >
                  {canContinuePayment ? ECOBANK_LOCALES.action.continue : ECOBANK_LOCALES.action.pay}
                </LockableButton>
              )}

              {isAgency && !invoice.is_paid && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <LockableButton
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shadow-none"
                        disabled={isEcobankLocked}
                        onClick={(event) => {
                          event.stopPropagation();
                          onDelete(invoice, ecobankPaymentState);
                        }}
                        aria-label={
                          isEcobankLocked
                            ? `${ECOBANK_LOCALES.expanded.delete_locked_aria} ${invoice.code}`
                            : `Supprimer la facture ${invoice.code}`
                        }
                      >
                        <Trash className="size-4 text-red-600" />
                      </LockableButton>
                    </span>
                  </TooltipTrigger>
                  {isEcobankLocked && (
                    <TooltipContent>
                      <p>{ECOBANK_LOCALES.expanded.delete_locked}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              )}

              {isBank && !invoice.is_paid && !invoice.quota_reached_error && (
                <LockableButton
                  variant="hajj"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    onActivate(invoice);
                  }}
                >
                  {Locales.base.button.confirm}
                </LockableButton>
              )}
            </div>
          </TableCell>
        )}
      </TableRow>

      <TableRow id={detailId} className="bg-[#fafbfa] hover:bg-[#fafbfa]" hidden={!isExpanded}>
        <TableCell colSpan={columnCount} className="p-0 whitespace-normal">
          <div className="py-2 pl-[60px] pr-3">
            <h3 className="mb-1 text-sm font-medium text-[#34483a]">Pèlerins associés ({pilgrimCount})</h3>

            <AssociatedPilgrimsList
              trips={invoice.trips}
              fallbackCount={invoice.deposit_count}
              invoiceCode={invoice.code}
            />
            {ecobankExpandedCopy && (
              <div className="mt-3 flex max-w-[730px] gap-3 border-t border-[#e2e7e4] pt-3" role="status">
                <div>
                  <p className="text-sm font-medium text-[#34483d]">{ECOBANK_LOCALES.expanded.label}</p>
                  <p className="text-sm font-medium text-[#334b5d]">{ecobankExpandedCopy.title}</p>
                  <p className="mt-0.5 text-sm leading-5 text-[#738079]">{ecobankExpandedCopy.description}</p>
                </div>
              </div>
            )}
          </div>
        </TableCell>
      </TableRow>
    </>
  );
};

InvoiceItem.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    code: PropTypes.string.isRequired,
    agency_name: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deposit_count: PropTypes.number,
    created: PropTypes.string,
    paid: PropTypes.string,
    is_paid: PropTypes.bool,
    quota_reached_error: PropTypes.bool,
    trips: PropTypes.arrayOf(
      PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
  }).isRequired,
  isAgency: PropTypes.bool,
  isAgencyPrimary: PropTypes.bool,
  isBank: PropTypes.bool,
  ecobankPaymentState: PropTypes.oneOf(Object.values(ECOBANK_PAYMENT_STATE)).isRequired,
  onDelete: PropTypes.func.isRequired,
  onActivate: PropTypes.func.isRequired,
  onEcobankAction: PropTypes.func.isRequired,
  invoiceValidationMethod: PropTypes.func.isRequired,
};

const Invoice = ({ ecobankDevelopment = ECOBANK_DEVELOPMENT }) => {
  const { isAGLO, isAgency, isAgencyPrimary, isBank, activeOrganizationId } = useGetUser();
  const { api } = useAPI();
  const {
    selectedCampaignYear,
    params: campaignParams,
    scope: campaignScope,
    isHistoricalCampaign,
    isCampaignSwitching,
  } = useAGLOCampaign();
  const [cursor, setCursor] = useState('');
  const [cursorYear, setCursorYear] = useState(selectedCampaignYear);
  const effectiveCursor = isAGLO && cursorYear !== selectedCampaignYear ? '' : cursor;
  const [searchParams, setSearchParams] = useSearchParams();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openActivationModal, setOpenActivationModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [ecobankInvoice, setEcobankInvoice] = useState(null);
  const [openEcobankModal, setOpenEcobankModal] = useState(false);
  const [ecobankModalState, setEcobankModalState] = useState(ECOBANK_PAYMENT_STATE.FRESH);
  const [ecobankStates, setEcobankStates] = useState({});
  const developmentEcobankState = resolveEcobankPaymentState(ecobankDevelopment.flags);
  const getInvoiceEcobankState = (invoice) => {
    if (invoice?.is_paid) return ECOBANK_PAYMENT_STATE.CONFIRMED;
    if (invoice && ecobankStates[invoice.id]) return ecobankStates[invoice.id];
    if (invoice && ecobankDevelopment.invoiceId === invoice.id) return developmentEcobankState;
    return ECOBANK_PAYMENT_STATE.FRESH;
  };

  const preSetSearch = searchParams.get('q') || '';
  const preSetAgency = isAGLO ? searchParams.get(CONST_FILTER_LABEL_AGENCY) || CONST_NONE : CONST_NONE;

  const [search, setSearch] = useState(preSetSearch);
  const [selectedAgency, setAgency] = useState(preSetAgency);

  const debouncedSearchQuery = useDebounce(search, 1000);
  const [queryFilters, setQueryFilter] = useState(() => getInvoiceAppliedFilters(preSetAgency));
  const queryClient = useQueryClient();
  const appliedAgency = queryFilters?.[CONST_FILTER_LABEL_AGENCY] || CONST_NONE;
  const effectiveSearchQuery = search ? debouncedSearchQuery : '';
  const fetchParams = useMemo(
    () => ({
      ...(queryFilters || {}),
      ...(effectiveSearchQuery ? { q: effectiveSearchQuery } : {}),
      ...(effectiveCursor ? { cursor: effectiveCursor } : {}),
      ...campaignParams,
    }),
    [campaignParams.campaign_year, effectiveCursor, effectiveSearchQuery, queryFilters]
  );
  const paginationKey = useMemo(
    () => JSON.stringify({ ...(queryFilters || {}), q: search || '', campaign: isAGLO ? selectedCampaignYear : null }),
    [isAGLO, queryFilters, search, selectedCampaignYear]
  );

  const invoiceQuery = useQuery({
    queryFn: api.list('invoice/', fetchParams),
    queryKey: ['invoice/', fetchParams, ...campaignScope],
    keepPreviousData: true,
  });

  const { data: previousInvoices, isFetching } = invoiceQuery;
  const invoices = isAGLO && isCampaignSwitching && invoiceQuery.isPreviousData ? undefined : previousInvoices;
  const agenciesQuery = useQuery({
    queryFn: api.list('agency/', campaignParams),
    queryKey: ['agency/', campaignParams, ...campaignScope],
    enabled: isAGLO,
  });

  const { data: agencies } = agenciesQuery;
  const invoiceStatsQuery = useQuery({
    queryFn: api.list('invoice/stats/deposit_count/', campaignParams),
    queryKey: ['invoice/stats/deposit_count/', campaignParams, ...campaignScope],
    enabled: !isAgency,
  });

  const { data: invoiceStatsData } = invoiceStatsQuery;
  useCampaignSwitchStatus(isAGLO ? [invoiceQuery, agenciesQuery, invoiceStatsQuery] : []);

  const invoiceDepositCountStatArr = invoiceStatsData?.data.data;

  const invoiceDepositCountStat =
    invoiceDepositCountStatArr && invoiceDepositCountStatArr.length > 0 ? invoiceDepositCountStatArr[0] : {};

  const { data: categoriesData } = useQuery({
    queryFn: api.list('category/'),
    queryKey: ['category/'],
  });

  const categories = categoriesData ? [...categoriesData].reverse() : [];

  const occupiedSeats = categories
    ? categories.map((category) => category.occupied_seats).reduce((sum, seats) => sum + seats, 0)
    : 0;

  const remainingSeats = CONST_TOTAL_SEATS - occupiedSeats;

  const displayQuotaAlert = !isHistoricalCampaign && invoiceDepositCountStat >= remainingSeats;

  const exportListParams = useMemo(
    () =>
      new URLSearchParams({
        ...(queryFilters || {}),
        ...(effectiveSearchQuery ? { q: effectiveSearchQuery } : {}),
        ...campaignParams,
      }).toString(),
    [campaignParams.campaign_year, effectiveSearchQuery, queryFilters]
  );

  const { mutate: deleteInvoice } = useMutation({
    mutationFn: (invoice) => api.remove('invoice', invoice.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice/'] });
      toast.success(Locales.invoice.toast.delete_invoice);
    },
  });

  const { mutate: activateInvoice } = useMutation({
    mutationFn: (invoice) => api.post(`invoice/${invoice.id}/payment/`),
    onSuccess: (_, invoice) => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/category/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/trip_category_paid/'] });
      queryClient.invalidateQueries({ queryKey: ['invoice/'] });
      queryClient.invalidateQueries({ queryKey: ['category/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/groupes/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/', 'is_sa'] });
      toast.success(
        Locales.formatString(Locales.invoice.toast.activate_invoice, {
          code: <strong>{invoice.code}</strong>,
        })
      );
    },
  });

  const { mutate: exportInvoicesList } = useMutation({
    mutationFn: ({ requestParams }) => api.get(`invoice/export/csv/?${requestParams}`),
    onSuccess: ({ data }, { filename }) => {
      createDownloadable(data, filename, 'csv');
      toast.success('Téléchargement réussi');
    },
  });

  const handleDelete = (invoice, paymentState = developmentEcobankState) => {
    if (isEcobankTransactionLocked(paymentState)) return;
    setOpenDeleteModal(true);
    setSelectedItem(invoice);
  };

  const handleDeleteConfirm = () => {
    if (!selectedItem) return;
    const paymentState = getInvoiceEcobankState(selectedItem);
    if (isEcobankTransactionLocked(paymentState)) {
      setOpenDeleteModal(false);
      return;
    }
    deleteInvoice(selectedItem);
  };

  const handleEcobankAction = (invoice) => {
    const paymentState = getInvoiceEcobankState(invoice);
    setEcobankInvoice(invoice);
    setEcobankModalState(canStartEcobankPayment(paymentState) ? ECOBANK_PAYMENT_STATE.FRESH : paymentState);
    setOpenEcobankModal(true);
  };

  const handleEcobankStateChange = (nextState) => {
    if (!ecobankInvoice) return;
    setEcobankModalState(nextState);
    setEcobankStates((previous) => ({ ...previous, [ecobankInvoice.id]: nextState }));
  };

  const handleEcobankModalChange = (nextOpen) => {
    setOpenEcobankModal(nextOpen);
    if (!nextOpen) setEcobankInvoice(null);
  };

  const handleActivation = (invoice) => {
    setOpenActivationModal(true);
    setSelectedItem(invoice);
  };

  const invoiceValidationMethod = (invoice) => {
    let result = '-';
    if (invoice.bank_name) {
      result = invoice.bank_name;
    } else if (invoice.payment_method === 'MOBILE_MONEY') {
      result = 'Mobile Money';
    } else if (invoice.payment_method === 'CARD') {
      result = 'Carte Bancaire';
    }

    return result;
  };

  const resultString = invoices
    ? `${invoices.count} ${
        invoices.count <= 1 ? Locales.invoice.title.toLowerCase().slice(0, -1) : Locales.invoice.title.toLowerCase()
      }`
    : '-';

  const selectedAgencyName = () => {
    if (appliedAgency === CONST_NONE) {
      return null;
    }

    return agencies?.find(({ id }) => id === appliedAgency)?.name || null;
  };

  const applyFilters = () => {
    let filters = null;

    if (selectedAgency !== CONST_NONE) {
      filters = { [CONST_FILTER_LABEL_AGENCY]: selectedAgency };
    }

    setQueryFilter(filters);
  };

  const resetFilters = () => {
    setAgency(CONST_NONE);
    setQueryFilter(null);
  };

  useEffect(() => {
    setSearch(preSetSearch);
    setAgency(preSetAgency);
    setQueryFilter(getInvoiceAppliedFilters(preSetAgency));
  }, [preSetAgency, preSetSearch]);

  useEffect(() => {
    let params = {};

    if (queryFilters?.[CONST_FILTER_LABEL_AGENCY]) {
      params = { [CONST_FILTER_LABEL_AGENCY]: queryFilters[CONST_FILTER_LABEL_AGENCY] };
    }

    if (search) {
      params = { ...params, q: search };
    }

    if (Object.keys(params).length > 0) {
      setSearchParams(params);
      return;
    }

    setSearchParams({});
  }, [queryFilters, search, setSearchParams]);

  useEffect(() => {
    setCursor('');
    if (isAGLO && cursorYear !== selectedCampaignYear) {
      setCursorYear(selectedCampaignYear);
      setSelectedItem(null);
      setOpenDeleteModal(false);
      setOpenActivationModal(false);
    }
  }, [cursorYear, isAGLO, paginationKey, selectedCampaignYear]);

  useEffect(() => {
    setOpenEcobankModal(false);
    setEcobankInvoice(null);
    setEcobankModalState(ECOBANK_PAYMENT_STATE.FRESH);
    setOpenDeleteModal(false);
    setOpenActivationModal(false);
    setSelectedItem(null);
    setEcobankStates({});
  }, [activeOrganizationId, isAgencyPrimary]);

  return (
    <Page
      title="Factures"
      headerContent={<HistoricalCampaignStrip />}
      description="Liste des paiements effectués par les sociétés"
      onSearch={setSearch}
      searchPlaceholder="Rechercher un paiement..."
      bodyClassName="[&>div:empty]:hidden"
      removeContentStyles
      onPrint={
        invoices && invoices.length > 0
          ? () =>
              exportInvoicesList({
                filename: isAGLO ? `Liste des factures_${selectedCampaignYear}.csv` : 'Liste des factures',
                requestParams: exportListParams,
              })
          : undefined
      }
      printTooltip="Télécharger la liste des factures"
    >
      {!invoices && <EmptyCard />}

      {invoices && (
        <>
          {displayQuotaAlert && (
            <Alert variant="destructive" className="mb-4">
              <TriangleAlert className="size-[16px]" />

              <AlertTitle>
                Attention : si tous les paiements en attente sont reçus, le quota sera atteint ou dépassé.
              </AlertTitle>
            </Alert>
          )}

          <div className="overflow-hidden rounded-[10px] border border-[#dfe5e1] bg-white">
            <div className="px-5 pt-[19px]">
              <div className="flex items-center gap-2.5">
                <div className="relative min-w-0 flex-1">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#7c8881]"
                    aria-hidden="true"
                  />
                  <Input
                    type="search"
                    aria-label={Locales.invoice.search}
                    placeholder={Locales.invoice.search}
                    className="h-10 rounded-md border-[#d8e0db] pl-9 text-sm hover:border-[#b9c6be]"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={preventSubmitWithENTER}
                  />
                </div>

                {isAGLO && agencies && (
                  <DropdownCard>
                    <DropdownCard.Trigger>
                      <Button
                        variant="outline"
                        type="button"
                        className={`h-10 rounded-md px-3.5 text-sm shadow-none hover:bg-[#f6f8f6] ${
                          appliedAgency !== CONST_NONE
                            ? 'border-[#a9c8b5] bg-[#f0f7f2] text-[#09624f]'
                            : 'border-[#d7dfda] text-[#33463b]'
                        }`}
                      >
                        <ListFilter className="size-4" aria-hidden="true" />
                        {Locales.invoice.dropdown.filter}
                      </Button>
                    </DropdownCard.Trigger>
                    <DropdownCard.Dropdown width="large">
                      <DropdownCard.Content>
                        <FilterSelect
                          label={Locales.invoice.dropdown.agreed_agency}
                          helpText={Locales.invoice.dropdown.agreed_agency_helptext}
                          value={selectedAgency}
                          data={[{ label: Locales.base.button.select, value: CONST_NONE, disabled: true }].concat(
                            selectedAgency !== CONST_NONE && !agencies.some(({ id }) => id === selectedAgency)
                              ? [
                                  { id: selectedAgency, name: `Société ${selectedAgency} (absente de cette campagne)` },
                                  ...agencies,
                                ]
                              : agencies
                          )}
                          onChange={setAgency}
                        />
                      </DropdownCard.Content>
                      <DropdownCard.Actions>
                        <Button variant="link" onClick={resetFilters}>
                          {Locales.base.button.reset}
                        </Button>
                        <Button variant="hajj" onClick={applyFilters}>
                          {Locales.base.button.validate}
                        </Button>
                      </DropdownCard.Actions>
                    </DropdownCard.Dropdown>
                  </DropdownCard>
                )}
              </div>

              <div
                className="flex min-h-12 flex-wrap items-center gap-3 py-2.5 text-sm text-[#657269]"
                role="status"
                aria-live="polite"
              >
                <span>
                  {Locales.base.result} : <strong className="font-semibold text-[#34463b]">{resultString}</strong>
                </span>
                {queryFilters && Object.prototype.hasOwnProperty.call(queryFilters, CONST_FILTER_LABEL_AGENCY) && (
                  <span className="inline-flex h-7 max-w-full items-center gap-1 rounded-md border border-[#dbe7df] bg-[#f6faf7] pl-2 text-sm text-[#366449]">
                    <span className="truncate">{`${Locales.invoice.dropdown.agreed_agency}: ${
                      selectedAgencyName() || 'Société sélectionnée'
                    }`}</span>
                    <button
                      type="button"
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-md hover:bg-[#e3eee7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={resetFilters}
                      aria-label={`Retirer le filtre ${Locales.invoice.dropdown.agreed_agency}`}
                    >
                      <X className="size-3.5" aria-hidden="true" />
                    </button>
                  </span>
                )}
              </div>
            </div>

            {invoices?.length === 0 ? (
              <div className="border-t border-[#dfe5e1] p-8 text-center text-sm font-bold text-gray-700">
                Aucune donnée disponible
              </div>
            ) : (
              <Table
                className={`${isAgency || isBank ? 'min-w-[1420px]' : 'min-w-[1280px]'} table-fixed text-sm`}
                containerClassName="border-t border-[#dfe5e1]"
                containerProps={{ role: 'region', 'aria-label': 'Tableau des factures', tabIndex: 0 }}
              >
                <caption className="sr-only">Factures et pèlerins associés</caption>
                <colgroup>
                  <col className="w-12" />
                  <col className="w-28" />
                  <col className="w-40" />
                  <col className="w-40" />
                  <col className="w-[72px]" />
                  <col className="w-36" />
                  <col className="w-36" />
                  <col className="w-36" />
                  <col className="w-52" />
                  {(isAgency || isBank) && <col className="w-56" />}
                </colgroup>
                <TableHeader className="bg-[#f7f9f8]">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-[42px] px-0 py-0">
                      <span className="sr-only">Pèlerins associés</span>
                    </TableHead>
                    <TableHead className="h-[42px] px-3 py-0 text-sm">Référence</TableHead>
                    <TableHead className="h-[42px] px-3 py-0 text-sm">Société</TableHead>
                    <TableHead className="h-[42px] px-3 py-0 text-right text-sm">Montant</TableHead>
                    <TableHead className="h-[42px] px-3 py-0 text-center text-sm">Dépôts</TableHead>
                    <TableHead className="h-[42px] px-3 py-0 text-sm">Date de création</TableHead>
                    <TableHead className="h-[42px] px-3 py-0 text-sm">Date de paiement</TableHead>
                    <TableHead className="h-[42px] px-3 py-0 text-sm">Validé par</TableHead>
                    <TableHead className="h-[42px] px-3 py-0 text-sm">Statut</TableHead>
                    {(isAgency || isBank) && <TableHead className="h-[42px] px-3 py-0 text-sm">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <InvoiceItem
                      key={invoice.id}
                      invoice={invoice}
                      isAgency={isAgency}
                      isAgencyPrimary={isAgencyPrimary}
                      isBank={isBank}
                      ecobankPaymentState={getInvoiceEcobankState(invoice)}
                      onDelete={handleDelete}
                      onActivate={handleActivation}
                      onEcobankAction={handleEcobankAction}
                      invoiceValidationMethod={invoiceValidationMethod}
                    />
                  ))}
                </TableBody>
              </Table>
            )}

            <div className="[&>nav]:text-sm [&_button]:text-sm">
              <Pagination key={paginationKey} data={invoices} isFetching={isFetching} setCursor={setCursor} />
            </div>
          </div>
        </>
      )}

      {selectedItem && (
        <>
          <ConfirmationModal
            isOpen={openDeleteModal}
            setOpenModal={setOpenDeleteModal}
            title={Locales.invoice.action_modal.title.delete}
            confirmBtnVariant="destructive"
            confirmBtnLabel={Locales.base.button.delete}
            message={
              <p>
                {Locales.formatString(Locales.invoice.delete_payment, {
                  amount: <strong>{formatNumber(selectedItem.amount)}</strong>,
                  code: <strong>{selectedItem.code}</strong>,
                })}
              </p>
            }
            onConfirm={handleDeleteConfirm}
          />

          <ConfirmationModal
            isOpen={openActivationModal}
            setOpenModal={setOpenActivationModal}
            title={Locales.invoice.action_modal.title.validation}
            confirmBtnLabel={Locales.base.button.confirm}
            message={
              <p>
                {Locales.formatString(Locales.invoice.confirm_payment, {
                  amount: <strong>{formatNumber(selectedItem.amount)}</strong>,
                  code: <strong>{selectedItem.code}</strong>,
                })}
              </p>
            }
            onConfirm={() => activateInvoice(selectedItem)}
          />
        </>
      )}

      <EcobankPaymentModal
        invoice={ecobankInvoice}
        isOpen={openEcobankModal}
        onOpenChange={handleEcobankModalChange}
        paymentState={ecobankModalState}
        maskedAccount={ecobankDevelopment.maskedAccount}
        safeRejectionReason={ecobankDevelopment.safeRejectionReason}
        onPaymentStateChange={handleEcobankStateChange}
      />
    </Page>
  );
};

Invoice.propTypes = {
  ecobankDevelopment: PropTypes.shape({
    invoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    flags: PropTypes.objectOf(PropTypes.bool),
    maskedAccount: PropTypes.string,
    safeRejectionReason: PropTypes.string,
  }),
};

export default Invoice;
